/**
 * Restricts the analyzer's quality verdict to what the uploaded file can
 * actually be guilty of.
 *
 * `docs/spec/midi-quality-feedback.md` was written for a **performance MIDI**:
 * its flags ask whether a piano track could be found among drums and vocals,
 * whether a program change was ever sent, whether the polyphony looks like a
 * bare melody export. An **engraved score** answers none of those questions —
 * it has staves, not tracks, and no program changes at all — while carrying
 * caveats of its own (a D.C. that is not followed, more than two staves, cue
 * notes that sound).
 *
 * Both families travel through the same `quality` field, and the field alone
 * cannot say which one it describes. The format can: the front knows it from
 * the file the user picked, before any round trip. So this module is where a
 * flag is checked against the format it claims to describe, and dropped when
 * the two do not match.
 *
 * The rule is deliberately one-sided. We only suppress what we *know* is
 * meaningless for the format at hand — a code the front has never heard of is
 * kept and surfaced with the generic wording, because swallowing a signal the
 * server took the trouble to send is the worse failure.
 */
import type { QualityFlag, QualityStatus } from "@/lib/music/analyzer-types";

import type { ScoreFormat } from "./score-file";

/**
 * Flags a given format cannot meaningfully carry.
 *
 * On **MusicXML**: `single_track_no_program` is about MIDI Program Change
 * messages. A score has none, ever, so the flag would fire on every one.
 *
 * On **MIDI**: `jumps_not_followed`, which needs an engraver to exist — a MIDI
 * has no written repeat to skip.
 *
 * `polyphony_low` was listed here at first, on the reading "few notes,
 * probably a melody export". That is the MIDI test, not the score one: the
 * analyser measures a part's *simultaneity* and pairs it with the same note
 * floor on both sides, so it fires on a score that really is one short
 * unaccompanied line — which is not piano material whatever produced it.
 * Suppressing it here would have swallowed a true answer.
 *
 * Everything not listed describes the *music* rather than the encoding — "no
 * piano in this file", "mostly drums", "it's a full band arrangement" — and
 * stays true of a multi-part score as much as of a multi-track MIDI.
 */
const NOT_APPLICABLE: Record<ScoreFormat, readonly QualityFlag[]> = {
  midi: ["jumps_not_followed"],
  musicxml: ["single_track_no_program"],
};

/** True when `flag` can describe a file of this format. */
export function qualityFlagAppliesTo(
  flag: QualityFlag,
  format: ScoreFormat,
): boolean {
  return !NOT_APPLICABLE[format].includes(flag);
}

/**
 * Returns the flags worth showing, or the input array itself when none was
 * dropped. The identity return is what lets the caller tell "nothing to do"
 * from "everything was dropped" without a second pass.
 */
function keepApplicable(
  flags: readonly QualityFlag[],
  format: ScoreFormat,
): readonly QualityFlag[] {
  const kept = flags.filter((flag) => qualityFlagAppliesTo(flag, format));

  return kept.length === flags.length ? flags : kept;
}

/**
 * Rewrites a verdict so it only speaks about things this format can exhibit.
 *
 * A verdict whose every flag was dropped collapses to `ok`: there is nothing
 * left to tell the user, so no warning block, no toast, and — for a
 * `needs_help` — no help screen either. A verdict that arrives without flags
 * is out of contract (spec §3.1 requires at least one) and is returned
 * untouched rather than silently downgraded.
 */
export function scopeQualityToFormat(
  quality: QualityStatus,
  format: ScoreFormat,
): QualityStatus {
  if (quality.kind === "ok") {
    return quality;
  }

  if (quality.kind === "ok_with_hints") {
    const hints = keepApplicable(quality.hints, format);

    if (hints === quality.hints) {
      return quality;
    }

    return hints.length === 0 ? { kind: "ok" } : { kind: "ok_with_hints", hints: [...hints] };
  }

  const flags = keepApplicable(quality.flags, format);

  if (flags === quality.flags) {
    return quality;
  }

  return flags.length === 0 ? { kind: "ok" } : { kind: "needs_help", flags: [...flags] };
}
